import { checkPinClient } from "../pin.js";
import { Banner, Button, PinPad } from "@omony/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useIntl } from "react-intl";
import { api, errorMessageId } from "../../shared/api.js";
import { refreshSession } from "../../shared/query.js";
import styles from "../console.module.css";

/** Nach einem PIN-Reset legt die Person ihre eigene PIN fest, bevor sie arbeiten kann. */
export function PinChangeScreen({ currentPin, onLock }: { currentPin: string | null; onLock: () => void }) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [stage, setStage] = useState<"current" | "new" | "repeat">(currentPin ? "new" : "current");
  const [current, setCurrent] = useState(currentPin ?? "");
  const [next, setNext] = useState("");
  const [value, setValue] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: (newPin: string) =>
      api("/device/pin", { method: "POST", body: { currentPin: current, newPin } }),
    onSuccess: () => refreshSession(),
    onError: () => {
      setStage(currentPin ? "new" : "current");
      setValue("");
      setNext("");
    },
  });
  const submit = (v: string) => {
    setLocalError(null);
    if (stage === "current") {
      setCurrent(v);
      setStage("new");
    } else if (stage === "new") {
      const problem = checkPinClient(v, current);
      if (problem) {
        setLocalError(t(`error.${problem}`));
      } else {
        setNext(v);
        setStage("repeat");
      }
    } else if (v !== next) {
      setLocalError(t("console.pin.mismatch"));
      setStage("new");
      setNext("");
    } else m.mutate(v);
    setValue("");
  };
  return (
    <main id="main" className={styles.lockPage}>
      <div className={styles.lockCard}>
        <div className={styles.lockHead}>
          <span className={styles.eyebrow}>{t("console.pin.eyebrow")}</span>
          <h1 className={styles.lockTitle}>{t(`console.pin.${stage}`)}</h1>
          <p className={styles.muted}>{t("console.pin.lead")}</p>
        </div>
        {localError ? <Banner tone="danger">{localError}</Banner> : null}
        {m.error ? <Banner tone="danger">{t(errorMessageId(m.error))}</Banner> : null}
        <PinPad
          label={t(`console.pin.${stage}`)}
          value={value}
          onChange={setValue}
          onSubmit={submit}
          isDisabled={m.isPending}
        />
        <Button variant="quiet" onPress={onLock}>
          {t("console.lock.lockNow")}
        </Button>
      </div>
    </main>
  );
}
